<script setup lang="ts">
import { computed, reactive, ref, toRaw } from 'vue';

import { isWorkingHours } from '@/utils/date';
import KBtn from '@/components/btns/KBtn.vue';
import KTextField from '@/components/fields/KTextField.vue';
import useForm from '@/composables/useForm';
import { useUserStore } from '@/stores/useUserStore';
import { requiredValidationRule } from '@/helpers/validationRules';

export interface InitialData {
  message?: string;
  attachments?: File | File[];
}

export interface ValidData {
  message: string;
  attachments: File | File[];
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});

const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(
  props,
  formData,
  () => {
    emit('submit', toRaw(formData) as ValidData);
  },
  { resetOnSubmit: true },
);

const fileRules = [
  (files: File[]) => {
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!files) return true;
    const maxSize = 10 * 1024 * 1024; // 10MB
    return (
      files.every((file) => file.size <= maxSize) ||
      'File size should not exceed 10MB'
    );
  },
];

const userStore = useUserStore();
const userTimeZone = computed(() => userStore.user.timezone);
const hours = { start: 9, end: 17 };

const formatWorkingHours = (): string => {
  return '9 AM - 5 PM AEDT (Sydney), Monday to Friday';
};

const handleFileChange = (files: File | File[]): void => {
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  formData.attachments = files || [];
};
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.helpcenterSideBarChatForm"
    data-testid="helpcenterSideBarChatForm"
    @submit.prevent="handleSubmit"
  >
    <v-alert
      v-if="!isWorkingHours(hours, userTimeZone)"
      closable
      type="info"
      class="ma-2"
      density="compact"
    >
      Our support team is currently offline. We'll respond to your message
      during working hours ({{ formatWorkingHours() }}).
    </v-alert>
    <div :class="$style.inputContainer">
      <KTextField
        v-model="formData.message"
        size="lg"
        label="Type message..."
        :disabled="props.loading"
        :rules="[requiredValidationRule]"
        :class="$style.field"
        hide-details
        autocomplete="off"
      />
      <KBtn
        type="submit"
        size="lg"
        priority="info"
        icon="mdi-send"
        :loading="props.loading"
        :class="$style.submit"
      />
    </div>
    <v-file-input
      multiple
      hide-details
      density="comfortable"
      variant="outlined"
      prepend-icon="mdi-paperclip"
      label="Attach files"
      :class="$style.fileInput"
      :rules="fileRules"
      @update:model-value="handleFileChange"
    />
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.helpcenterSideBarChatForm {}

  .inputContainer {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
  }
  .fileInput {
    max-width: 100%;
    width: 100%;
  }
</style>
